import { DashboardLayoutContainer } from 'components/Layouts/DashboardLayout';
import { Box } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import { getPolicyAction } from 'store/actions/propertyActions';
import { PolicyTable } from 'components/Tables/PolicyTable';
import { Button, Loading } from '@leapeasy/ui-kit';
import { openDetails } from 'store/actions/uiActions';
import { failedNotif } from 'store/actions/notifActions';
import { getPolicy } from 'store/sagas/APIs/property';
import { useListQueryState } from 'hooks/useListQueryState';
import { useClampListPagination } from 'hooks/useClampListPagination';
import { usePaginatedListFetch } from 'hooks/usePaginatedListFetch';
import { useLazyExportDatasetTotal } from 'hooks/useLazyExportDatasetTotal';
import { fetchAllPaginated } from 'utils/exportPagination';
import { ApplicationListActiveFilters } from 'components/ApplicationListActiveFilters/ApplicationListActiveFilters';
import {
  buildPolicyListFilterChips,
  parsePolicyListFilterChipId,
} from 'utils/propertyListActiveFilterChips';
import { PolicyListDropFilterFields } from 'components/PolicyList/PolicyListDropFilterFields';
import {
  DEFAULT_POLICY_LIST_EXTRA_FILTERS,
  areArraysEqual,
  areDateRangesEqual,
  arePolicyIssuedDateRangeFiltersEqual,
  buildPolicyListRequestFilter,
  normalizeArrayFilter,
  normalizeDateRangeFilter,
  normalizePolicyIssuedDateRangeFilter,
  parsePoliciesExtraFilters,
  resolvePolicyAppTypeFilterForApi,
  serializePoliciesFilters,
} from 'utils/policyListQueryFilters';
import { EMPTY_POLICY_ISSUED_DATE_RANGE_FILTER } from 'utils/policyIssuedDateRangeFilter';
import { GlobalUXState } from 'components/GlobalUXState';
import { getBreadcrumbParentsFromState } from 'utils/breadcrumbNavigation';
import { buildDetailNavigationState } from 'utils/detailBackNavigation';
import {
  POLICY_COVERAGE_STATUS_FILTER_OPTIONS,
  POLICY_LIST_APP_TYPE_FILTER_OPTIONS,
} from 'data/constants/common_constants';

const EMPTY_POLICY_LIST_FILTER = {
  searchText: '',
  options: {},
  selectedAppType: [],
  selectedIssuedDate: { ...EMPTY_POLICY_ISSUED_DATE_RANGE_FILTER },
  selectedCoverageStatus: [],
  selectedLeaseDate: { startDate: null, endDate: null },
  selectedTenantCount: [],
  activeLease: [],
  selectedExpiringRange: null,
  selectedLeaseBucket: null,
};

export const Policies = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    filter,
    setFilter,
    sortOptions,
    setSortOptions,
    pagination,
    setPagination,
    extraFilters,
    setExtraFilters,
  } = useListQueryState({
    defaultFilter: {
      options: {},
      searchText: '',
      searchPlaceholder: 'Search Policy..',
    },
    extraFiltersParam: 'filters',
    defaultExtraFilters: DEFAULT_POLICY_LIST_EXTRA_FILTERS,
    parseExtraFilters: parsePoliciesExtraFilters,
    serializeExtraFilters: serializePoliciesFilters,
  });
  const [selectedAppType, setSelectedAppType] = useState(() =>
    normalizeArrayFilter(extraFilters?.selectedAppType),
  );
  const [selectedIssuedDate, setSelectedIssuedDate] = useState(() =>
    normalizePolicyIssuedDateRangeFilter(extraFilters?.selectedIssuedDate),
  );
  const [selectedCoverageStatus, setSelectedCoverageStatus] = useState(() =>
    normalizeArrayFilter(extraFilters?.selectedCoverageStatus),
  );
  const [selectedLeaseDate, setSelectedLeaseDate] = useState(() =>
    normalizeDateRangeFilter(extraFilters?.selectedLeaseDate),
  );
  const [selectedTenantCount, setSelectedTenantCount] = useState(() =>
    normalizeArrayFilter(extraFilters?.selectedTenantCount),
  );
  const [activeLease, setActiveLease] = useState(() =>
    normalizeArrayFilter(extraFilters?.activeLease),
  );
  const [selectedAppTypeDraft, setSelectedAppTypeDraft] = useState(() =>
    normalizeArrayFilter(extraFilters?.selectedAppType),
  );
  const [selectedIssuedDateDraft, setSelectedIssuedDateDraft] = useState(() =>
    normalizePolicyIssuedDateRangeFilter(extraFilters?.selectedIssuedDate),
  );
  const [selectedCoverageStatusDraft, setSelectedCoverageStatusDraft] = useState(() =>
    normalizeArrayFilter(extraFilters?.selectedCoverageStatus),
  );
  const [selectedLeaseDateDraft, setSelectedLeaseDateDraft] = useState(() =>
    normalizeDateRangeFilter(extraFilters?.selectedLeaseDate),
  );
  const [selectedTenantCountDraft, setSelectedTenantCountDraft] = useState(() =>
    normalizeArrayFilter(extraFilters?.selectedTenantCount),
  );
  const [activeLeaseDraft, setActiveLeaseDraft] = useState(() =>
    normalizeArrayFilter(extraFilters?.activeLease),
  );
  const [selectedExpiringRange, setSelectedExpiringRange] = useState(
    () => extraFilters?.selectedExpiringRange || null,
  );
  const [selectedLeaseBucket, setSelectedLeaseBucket] = useState(
    () => extraFilters?.selectedLeaseBucket || null,
  );
  const apiAppTypeFilter = useMemo(
    () => resolvePolicyAppTypeFilterForApi(selectedAppType),
    [selectedAppType],
  );

  useEffect(() => {
    if (!extraFilters || typeof extraFilters !== 'object') return;
    const nextSelectedAppType = normalizeArrayFilter(extraFilters.selectedAppType);
    const nextSelectedIssuedDate = normalizePolicyIssuedDateRangeFilter(
      extraFilters.selectedIssuedDate,
    );
    const nextSelectedCoverageStatus = normalizeArrayFilter(extraFilters.selectedCoverageStatus);
    const nextSelectedLeaseDate = normalizeDateRangeFilter(extraFilters.selectedLeaseDate);
    const nextSelectedTenantCount = normalizeArrayFilter(extraFilters.selectedTenantCount);
    const nextActiveLease = normalizeArrayFilter(extraFilters.activeLease);
    const nextSelectedExpiringRange = extraFilters?.selectedExpiringRange || null;
    const nextSelectedLeaseBucket = extraFilters?.selectedLeaseBucket || null;
    setSelectedAppType((prev) =>
      areArraysEqual(prev, nextSelectedAppType) ? prev : nextSelectedAppType,
    );
    setSelectedIssuedDate((prev) =>
      arePolicyIssuedDateRangeFiltersEqual(prev, nextSelectedIssuedDate)
        ? prev
        : nextSelectedIssuedDate,
    );
    setSelectedCoverageStatus((prev) =>
      areArraysEqual(prev, nextSelectedCoverageStatus) ? prev : nextSelectedCoverageStatus,
    );
    setSelectedLeaseDate((prev) =>
      areDateRangesEqual(prev, nextSelectedLeaseDate) ? prev : nextSelectedLeaseDate,
    );
    setSelectedTenantCount((prev) =>
      areArraysEqual(prev, nextSelectedTenantCount) ? prev : nextSelectedTenantCount,
    );
    setActiveLease((prev) => (areArraysEqual(prev, nextActiveLease) ? prev : nextActiveLease));
    setSelectedAppTypeDraft((prev) =>
      areArraysEqual(prev, nextSelectedAppType) ? prev : nextSelectedAppType,
    );
    setSelectedIssuedDateDraft((prev) =>
      arePolicyIssuedDateRangeFiltersEqual(prev, nextSelectedIssuedDate)
        ? prev
        : nextSelectedIssuedDate,
    );
    setSelectedCoverageStatusDraft((prev) =>
      areArraysEqual(prev, nextSelectedCoverageStatus) ? prev : nextSelectedCoverageStatus,
    );
    setSelectedLeaseDateDraft((prev) =>
      areDateRangesEqual(prev, nextSelectedLeaseDate) ? prev : nextSelectedLeaseDate,
    );
    setSelectedTenantCountDraft((prev) =>
      areArraysEqual(prev, nextSelectedTenantCount) ? prev : nextSelectedTenantCount,
    );
    setActiveLeaseDraft((prev) => (areArraysEqual(prev, nextActiveLease) ? prev : nextActiveLease));
    setSelectedExpiringRange((prev) =>
      prev === nextSelectedExpiringRange ? prev : nextSelectedExpiringRange,
    );
    setSelectedLeaseBucket((prev) =>
      prev === nextSelectedLeaseBucket ? prev : nextSelectedLeaseBucket,
    );
  }, [extraFilters]);

  const listQuery = useMemo(
    () => ({
      filter: buildPolicyListRequestFilter({
        filter,
        selectedAppType: apiAppTypeFilter,
        selectedIssuedDate,
        selectedCoverageStatus,
        selectedLeaseDate,
        selectedTenantCount,
        activeLease,
        selectedExpiringRange,
        selectedLeaseBucket,
      }),
      offset: pagination.pageNumber * pagination.rowsPerPage,
      limit: pagination.rowsPerPage,
      sortOptions,
    }),
    [
      filter,
      apiAppTypeFilter,
      selectedIssuedDate,
      selectedCoverageStatus,
      selectedLeaseDate,
      selectedTenantCount,
      activeLease,
      selectedExpiringRange,
      selectedLeaseBucket,
      pagination.pageNumber,
      pagination.rowsPerPage,
      sortOptions,
    ],
  );

  usePaginatedListFetch({
    action: getPolicyAction,
    payload: listQuery,
  });

  const refetchList = useCallback(() => {
    dispatch(getPolicyAction(listQuery));
  }, [dispatch, listQuery]);

  const fetchFullExportDatasetTotal = useCallback(async () => {
    const response = await getPolicy({
      filter: EMPTY_POLICY_LIST_FILTER,
      offset: 0,
      limit: 1,
      sortOptions,
    });
    return response?.total != null ? Number(response.total) : undefined;
  }, [sortOptions]);

  const {
    fullExportDatasetTotal,
    isLoadingFullExportTotal,
    loadFullExportDatasetTotal,
    resetFullExportDatasetTotal,
  } = useLazyExportDatasetTotal(fetchFullExportDatasetTotal);

  useEffect(() => {
    resetFullExportDatasetTotal();
  }, [sortOptions, resetFullExportDatasetTotal]);

  const successMsg = useSelector((state) => state.getIn(['notification', 'successMsg']));
  const successText = successMsg == null ? '' : String(successMsg);

  useEffect(() => {
    if (!successText || successText === 'loading') return;
    if (!successText.toLowerCase().includes('cancelled')) return;
    refetchList();
  }, [successText, refetchList]);

  const policies = useSelector((state) => state.getIn(['property', 'policy']));
  const policiesJSON = policies?.toJS?.() ?? null;
  const policyListError = policiesJSON?.policyError;

  const APPTYPE = POLICY_LIST_APP_TYPE_FILTER_OPTIONS;
  const COVERAGE_STATUS = POLICY_COVERAGE_STATUS_FILTER_OPTIONS;

  const resetFunc = useCallback(() => {
    setSelectedAppType([]);
    setSelectedIssuedDate({ ...EMPTY_POLICY_ISSUED_DATE_RANGE_FILTER });
    setSelectedCoverageStatus([]);
    setSelectedLeaseDate({
      startDate: null,
      endDate: null,
    });
    setSelectedTenantCount([]);
    setActiveLease([]);
    setSelectedAppTypeDraft([]);
    setSelectedIssuedDateDraft({ ...EMPTY_POLICY_ISSUED_DATE_RANGE_FILTER });
    setSelectedCoverageStatusDraft([]);
    setSelectedLeaseDateDraft({
      startDate: null,
      endDate: null,
    });
    setSelectedTenantCountDraft([]);
    setActiveLeaseDraft([]);
    setSelectedExpiringRange(null);
    setExtraFilters({
      selectedAppType: [],
      selectedIssuedDate: { ...EMPTY_POLICY_ISSUED_DATE_RANGE_FILTER },
      selectedCoverageStatus: [],
      selectedLeaseDate: {
        startDate: null,
        endDate: null,
      },
      selectedTenantCount: [],
      activeLease: [],
      selectedExpiringRange: null,
      selectedLeaseBucket: null,
    });
    setFilter((prev) => ({
      ...prev,
      searchText: '',
    }));
    setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
  }, [setExtraFilters, setFilter, setPagination]);

  const applyFunc = useCallback(() => {
    setSelectedAppType(selectedAppTypeDraft);
    setSelectedIssuedDate(selectedIssuedDateDraft);
    setSelectedCoverageStatus(selectedCoverageStatusDraft);
    setSelectedLeaseDate(selectedLeaseDateDraft);
    setSelectedTenantCount(selectedTenantCountDraft);
    setActiveLease(activeLeaseDraft);
    setExtraFilters({
      selectedAppType: selectedAppTypeDraft,
      selectedIssuedDate: selectedIssuedDateDraft,
      selectedCoverageStatus: selectedCoverageStatusDraft,
      selectedLeaseDate: selectedLeaseDateDraft,
      selectedTenantCount: selectedTenantCountDraft,
      activeLease: activeLeaseDraft,
      selectedExpiringRange,
      selectedLeaseBucket,
    });
    setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
  }, [
    selectedAppTypeDraft,
    selectedIssuedDateDraft,
    selectedCoverageStatusDraft,
    selectedLeaseDateDraft,
    selectedTenantCountDraft,
    activeLeaseDraft,
    selectedExpiringRange,
    selectedLeaseBucket,
    setExtraFilters,
    setPagination,
  ]);

  const onClickPolicy = useCallback(
    (policy) => {
      if (!policy?.app_id) {
        dispatch(failedNotif('Policy detail ID is missing.'));
        return;
      }

      dispatch(
        openDetails({
          type: 'policy',
          data: policy,
        }),
      );
      const returnTo = `${location.pathname}${location.search}`;
      navigate(`/property/policies/${policy.app_id}/detail`, {
        state: buildDetailNavigationState(
          {
            breadcrumbParents: getBreadcrumbParentsFromState(location.state),
          },
          returnTo,
        ),
      });
    },
    [dispatch, navigate, location.pathname, location.search, location.state],
  );

  const handleRequestExportData = useCallback(async () => {
    const totalRecords = Number(policiesJSON?.total) || 0;
    if (totalRecords === 0) return [];

    try {
      return await fetchAllPaginated({
        totalRecords,
        fetchPage: ({ offset, limit }) =>
          getPolicy({
            filter: buildPolicyListRequestFilter({
              filter,
              selectedAppType: apiAppTypeFilter,
              selectedIssuedDate,
              selectedCoverageStatus,
              selectedLeaseDate,
              selectedTenantCount,
              activeLease,
              selectedExpiringRange,
              selectedLeaseBucket,
            }),
            offset,
            limit,
            sortOptions,
          }),
      });
    } catch (error) {
      throw new Error('Unable to export the filtered policies right now.');
    }
  }, [
    policiesJSON?.total,
    filter,
    apiAppTypeFilter,
    selectedIssuedDate,
    selectedCoverageStatus,
    selectedLeaseDate,
    selectedTenantCount,
    activeLease,
    selectedExpiringRange,
    selectedLeaseBucket,
    sortOptions,
  ]);

  const handleRequestFullExportData = useCallback(async () => {
    const totalRecords =
      Number(fullExportDatasetTotal) || Number(await loadFullExportDatasetTotal()) || 0;
    if (totalRecords === 0) return [];

    try {
      return await fetchAllPaginated({
        totalRecords,
        fetchPage: ({ offset, limit }) =>
          getPolicy({
            filter: EMPTY_POLICY_LIST_FILTER,
            offset,
            limit,
            sortOptions,
          }),
      });
    } catch (error) {
      throw new Error('Unable to export all policies right now.');
    }
  }, [fullExportDatasetTotal, loadFullExportDatasetTotal, sortOptions]);

  const listFilterChips = useMemo(
    () =>
      buildPolicyListFilterChips({
        selectedAppType,
        selectedIssuedDate,
        selectedCoverageStatus,
        selectedLeaseDate,
        selectedTenantCount,
        activeLease,
        selectedExpiringRange,
        selectedLeaseBucket,
        appTypeOptions: APPTYPE,
        coverageStatusOptions: COVERAGE_STATUS,
      }),
    [
      selectedAppType,
      selectedIssuedDate,
      selectedCoverageStatus,
      selectedLeaseDate,
      selectedTenantCount,
      activeLease,
      selectedExpiringRange,
      selectedLeaseBucket,
      APPTYPE,
      COVERAGE_STATUS,
    ],
  );

  const handleRemovePolicyListFilterChip = useCallback(
    (chipId) => {
      const parsed = parsePolicyListFilterChipId(chipId);
      if (!parsed) return;

      if (parsed.type === 'issued') {
        const cleared = { ...EMPTY_POLICY_ISSUED_DATE_RANGE_FILTER };
        setSelectedIssuedDate(cleared);
        setSelectedIssuedDateDraft(cleared);
        setExtraFilters((prev) => ({ ...prev, selectedIssuedDate: cleared }));
        setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
        return;
      }

      if (parsed.type === 'coverageStatus') {
        const next = selectedCoverageStatus.filter((s) => s !== parsed.value);
        setSelectedCoverageStatus(next);
        setSelectedCoverageStatusDraft(next);
        setExtraFilters((prev) => ({ ...prev, selectedCoverageStatus: next }));
        setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
        return;
      }

      if (parsed.type === 'lease') {
        const cleared = { startDate: null, endDate: null };
        setSelectedLeaseDate(cleared);
        setSelectedLeaseDateDraft(cleared);
        setExtraFilters((prev) => ({ ...prev, selectedLeaseDate: cleared }));
        setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
        return;
      }

      if (parsed.type === 'expiring') {
        setSelectedExpiringRange(null);
        setExtraFilters((prev) => ({ ...prev, selectedExpiringRange: null }));
        setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
        return;
      }

      if (parsed.type === 'leaseBucket') {
        setSelectedLeaseBucket(null);
        setExtraFilters((prev) => ({ ...prev, selectedLeaseBucket: null }));
        setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
        return;
      }

      if (parsed.type === 'appType') {
        const next = selectedAppType.filter((s) => s !== parsed.value);
        setSelectedAppType(next);
        setSelectedAppTypeDraft(next);
        setExtraFilters((prev) => ({ ...prev, selectedAppType: next }));
        setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
        return;
      }

      if (parsed.type === 'tenant') {
        const next = selectedTenantCount.filter((s) => s !== parsed.value);
        setSelectedTenantCount(next);
        setSelectedTenantCountDraft(next);
        setExtraFilters((prev) => ({ ...prev, selectedTenantCount: next }));
        setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
        return;
      }

      if (parsed.type === 'activeLease') {
        const next = activeLease.filter((s) => s !== parsed.value);
        setActiveLease(next);
        setActiveLeaseDraft(next);
        setExtraFilters((prev) => ({ ...prev, activeLease: next }));
        setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
      }
    },
    [
      selectedAppType,
      selectedCoverageStatus,
      selectedTenantCount,
      activeLease,
      setExtraFilters,
      setPagination,
    ],
  );

  const listFilterActiveSummary = useMemo(() => {
    if (!listFilterChips.length) return null;
    return (
      <ApplicationListActiveFilters
        chips={listFilterChips}
        onRemoveChip={handleRemovePolicyListFilterChip}
        onClearAll={resetFunc}
      />
    );
  }, [listFilterChips, handleRemovePolicyListFilterChip, resetFunc]);

  const hasSearch = Boolean(`${filter?.searchText || ''}`.trim());
  const hasAppliedFilters = hasSearch || listFilterChips.length > 0;
  const policyEmptyContent = hasAppliedFilters ? (
    <GlobalUXState
      variant="noResults"
      description="No policies match the current filters."
      compact
      actionLabel="Clear filters"
      onAction={resetFunc}
    />
  ) : (
    <GlobalUXState
      variant="noData"
      title="No policies available"
      description="There are no policies to display yet."
      compact
    />
  );

  const policyTotal = Number(policiesJSON?.total) || 0;
  useClampListPagination({
    pagination,
    setPagination,
    totalItems: policyTotal,
    isLoading: Boolean(policiesJSON?.isLoading),
  });

  return (
    <DashboardLayoutContainer>
      {policiesJSON ? (
        <React.Fragment>
          <CustomBox>
            {policyListError && !policiesJSON.isLoading ? (
              <Box display="flex" flexDirection="column" alignItems="center" gap="16px" mb={2}>
                <GlobalUXState variant="apiError" compact description={policyListError} />
                <Button variant="primary" size="medium" onClick={refetchList}>
                  Retry
                </Button>
              </Box>
            ) : null}
            <PolicyTable
              policies={policiesJSON.data ?? []}
              clearFlagOnSameCoverageDay
              loading={policiesJSON.isLoading}
              showEmptyState={!policiesJSON.isLoading && !policyListError}
              emptyContent={policyEmptyContent}
              onRequestExportData={handleRequestExportData}
              onRequestFullExportData={handleRequestFullExportData}
              exportFullDatasetTotal={fullExportDatasetTotal}
              onLoadFullDatasetTotal={loadFullExportDatasetTotal}
              isLoadingFullDatasetTotal={isLoadingFullExportTotal}
              dropFilterActiveSummary={listFilterActiveSummary}
              dropFilterActiveCount={listFilterChips.length}
              filter={filter}
              onChangeFilter={setFilter}
              pagination={{
                ...pagination,
                totalItems: policyTotal,
                totalPages: Math.ceil(policyTotal / pagination.rowsPerPage) || 0,
              }}
              dropFilter={{
                component: (
                  <PolicyListDropFilterFields
                    appTypeOptions={[...APPTYPE]}
                    coverageStatusOptions={[...COVERAGE_STATUS]}
                    selectedAppTypeDraft={selectedAppTypeDraft}
                    onAppTypeDraftChange={setSelectedAppTypeDraft}
                    selectedIssuedDateDraft={selectedIssuedDateDraft}
                    onIssuedDateDraftChange={setSelectedIssuedDateDraft}
                    selectedCoverageStatusDraft={selectedCoverageStatusDraft}
                    onCoverageStatusDraftChange={setSelectedCoverageStatusDraft}
                    selectedLeaseDateDraft={selectedLeaseDateDraft}
                    onLeaseDateDraftChange={setSelectedLeaseDateDraft}
                    selectedTenantCountDraft={selectedTenantCountDraft}
                    onTenantCountDraftChange={setSelectedTenantCountDraft}
                    activeLeaseDraft={activeLeaseDraft}
                    onActiveLeaseDraftChange={setActiveLeaseDraft}
                  />
                ),
                resetFunc: resetFunc,
                applyFunc: applyFunc,
              }}
              onChangePagination={setPagination}
              sortOptions={sortOptions}
              onChangeSort={setSortOptions}
              onClickPolicy={onClickPolicy}
            />
          </CustomBox>
        </React.Fragment>
      ) : (
        <Loading size={'large'} label={''} />
      )}
    </DashboardLayoutContainer>
  );
};

const CustomBox = styled.div`
  position: relative;
`;
